import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase/config';
import { useNavigate } from 'react-router-dom';
import NotificationTestPanel from '../../components/NotificationTestPanel';
import { API_URL } from '../../config/api';

interface UserSettings {
  email: string;
  phoneNumber: string;
  profile: {
    size: string;
    measurements: {
      bust: number;
      waist: number;
      hips: number;
      height: number;
    };
    preferredStyles: string[];
    instagram: string;
    bio: string;
  };
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy: {
    showProfile: boolean;
    showLocation: boolean;
    showRating: boolean;
  };
  stripe: {
    connectedAccountId?: string;
    payoutsEnabled: boolean;
    chargesEnabled: boolean;
    detailsSubmitted: boolean;
    onboardingCompleted: boolean;
  };
}

const SettingsTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UserSettings>({
    email: '',
    phoneNumber: '',
    profile: {
      size: '',
      measurements: {
        bust: 0,
        waist: 0,
        hips: 0,
        height: 0
      },
      preferredStyles: [],
      instagram: '',
      bio: ''
    },
    notifications: {
      email: true,
      push: true,
      sms: false
    },
    privacy: {
      showProfile: true,
      showLocation: true,
      showRating: true
    },
    stripe: {
      payoutsEnabled: false,
      chargesEnabled: false,
      detailsSubmitted: false,
      onboardingCompleted: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (userData) {
        setSettings({
          email: user.email || '',
          phoneNumber: userData.phoneNumber || '',
          profile: {
            size: userData.profile?.size || '',
            measurements: {
              bust: userData.profile?.measurements?.bust || 0,
              waist: userData.profile?.measurements?.waist || 0,
              hips: userData.profile?.measurements?.hips || 0,
              height: userData.profile?.measurements?.height || 0
            },
            preferredStyles: userData.profile?.preferredStyles || [],
            instagram: userData.profile?.instagram || '',
            bio: userData.profile?.bio || ''
          },
          notifications: {
            email: userData.notifications?.email ?? true,
            push: userData.notifications?.push ?? true,
            sms: userData.notifications?.sms ?? false
          },
          privacy: {
            showProfile: userData.privacy?.showProfile ?? true,
            showLocation: userData.privacy?.showLocation ?? true,
            showRating: userData.privacy?.showRating ?? true
          },
          stripe: {
            connectedAccountId: userData.stripe?.connectedAccountId || '',
            payoutsEnabled: userData.stripe?.payoutsEnabled || false,
            chargesEnabled: userData.stripe?.chargesEnabled || false,
            detailsSubmitted: userData.stripe?.detailsSubmitted || false,
            onboardingCompleted: userData.stripe?.onboardingCompleted || false
          }
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        phoneNumber: settings.phoneNumber,
        profile: settings.profile,
        notifications: settings.notifications,
        privacy: settings.privacy,
        stripe: settings.stripe
      });
      setSuccess(true);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleActivatePayments = async () => {
    if (!user || !user.email) {
      setError('Email utilisateur manquant');
      return;
    }

    setStripeLoading(true);
    setError(null);
    setSuccess(false);

    try {
      console.log('🔵 [ACTIVATION] Appel API:', `${API_URL}/api/stripe/create-connected-account`);
      console.log('🔵 [ACTIVATION] UserId:', user.uid);
      console.log('🔵 [ACTIVATION] Email:', user.email);

      const response = await fetch(`${API_URL}/api/stripe/create-connected-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          userEmail: user.email
        })
      }).catch((fetchError) => {
        console.error('❌ [ACTIVATION] Erreur de connexion:', fetchError);
        throw new Error(`Impossible de se connecter au serveur (${API_URL}). Vérifiez que le backend est accessible et que CORS est configuré.`);
      });

      console.log('🔵 [ACTIVATION] Response status:', response.status);

      // Vérifier si la réponse est OK avant de parser
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur serveur' }));
        console.error('❌ [ACTIVATION] Erreur HTTP:', response.status, errorData);
        throw new Error(errorData.error || errorData.message || `Erreur serveur (${response.status})`);
      }

      const data = await response.json();
      console.log('🔵 [ACTIVATION] Response data:', data);

      if (data.success) {
        // Gérer les deux formats de réponse (snake_case et camelCase)
        const accountId = data.accountId || data.account_id;
        const onboardingUrl = data.onboardingUrl || data.onboarding_url;

        console.log('✅ [ACTIVATION] Compte Connect créé:', accountId);
        console.log('✅ [ACTIVATION] URL d\'onboarding:', onboardingUrl);

        if (!accountId || !onboardingUrl) {
          console.error('❌ [ACTIVATION] Données manquantes:', { accountId, onboardingUrl });
          setError('Données de réponse incomplètes. Veuillez réessayer.');
          setStripeLoading(false);
          return;
        }

        // Sauvegarder l'accountId en base
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            'stripe.connectedAccountId': accountId,
            'stripe.onboardingCompleted': false
          });
          console.log('✅ [ACTIVATION] AccountId sauvegardé en base');
        } catch (dbError) {
          console.error('⚠️ [ACTIVATION] Erreur sauvegarde en base (non bloquant):', dbError);
          // Ne pas bloquer la redirection si la sauvegarde échoue
        }

        // Mettre à jour le state
        setSettings(prev => ({
          ...prev,
          stripe: {
            ...prev.stripe,
            connectedAccountId: accountId
          }
        }));

        // Rediriger vers l'onboarding Stripe dans le même onglet
        console.log('🔵 [ACTIVATION] Redirection vers:', onboardingUrl);
        window.location.href = onboardingUrl;
      } else {
        console.error('❌ [ACTIVATION] Échec création compte:', data);
        const errorMessage = data.error || data.message || 'Erreur lors de l\'activation des paiements';
        setError(errorMessage);
        setStripeLoading(false);
      }
    } catch (error: any) {
      console.error('❌ [ACTIVATION] Erreur:', error);
      const errorMessage = error.message || 'Erreur lors de l\'activation des paiements. Vérifiez votre connexion et réessayez.';
      setError(errorMessage);
      setStripeLoading(false);
    }
  };

  const handleCheckStripeStatus = async () => {
    if (!user || !settings.stripe.connectedAccountId) return;
    setStripeLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/stripe/connected-account/${settings.stripe.connectedAccountId}`);
      const data = await response.json();

      if (data.success && data.account) {
        const account = data.account;
        const updatedStripe = {
          connectedAccountId: account.id,
          payoutsEnabled: account.payouts_enabled || false,
          chargesEnabled: account.charges_enabled || false,
          detailsSubmitted: account.details_submitted || false,
          onboardingCompleted: (account.charges_enabled && account.payouts_enabled) || false
        };

        // Mettre à jour en base
        await updateDoc(doc(db, 'users', user.uid), {
          stripe: updatedStripe
        });

        // Mettre à jour le state
        setSettings(prev => ({
          ...prev,
          stripe: updatedStripe
        }));

        setSuccess(true);
      } else {
        setError('Impossible de vérifier le statut du compte Stripe');
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      setError('Erreur lors de la vérification du statut');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleNotificationChange = (type: keyof UserSettings['notifications']) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type]
      }
    }));
  };

  const handlePrivacyChange = (type: keyof UserSettings['privacy']) => {
    setSettings(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [type]: !prev.privacy[type]
      }
    }));
  };

  const handleProfileChange = (field: keyof UserSettings['profile'], value: any) => {
    setSettings(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        [field]: value
      }
    }));
  };

  const handleMeasurementChange = (field: keyof UserSettings['profile']['measurements'], value: number) => {
    setSettings(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        measurements: {
          ...prev.profile.measurements,
          [field]: value
        }
      }
    }));
  };

  const handleStyleChange = (style: string) => {
    setSettings(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        preferredStyles: prev.profile.preferredStyles.includes(style)
          ? prev.profile.preferredStyles.filter(s => s !== style)
          : [...prev.profile.preferredStyles, style]
      }
    }));
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#C9B6AB' }}>
        Paramètres
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Informations de contact
        </Typography>
        <TextField
          fullWidth
          label="Email"
          value={settings.email}
          disabled
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Numéro de téléphone"
          value={settings.phoneNumber}
          onChange={(e) => setSettings(prev => ({ ...prev, phoneNumber: e.target.value }))}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#C9B6AB' }}>
          Compléter le profil
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ajoutez vos informations pour une meilleure expérience de location
        </Typography>

        <Grid container spacing={3}>
          {/* Taille */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Taille</InputLabel>
              <Select
                value={settings.profile.size}
                onChange={(e) => handleProfileChange('size', e.target.value)}
                label="Taille"
              >
                <MenuItem value="XS">XS</MenuItem>
                <MenuItem value="S">S</MenuItem>
                <MenuItem value="M">M</MenuItem>
                <MenuItem value="L">L</MenuItem>
                <MenuItem value="XL">XL</MenuItem>
                <MenuItem value="XXL">XXL</MenuItem>
                <MenuItem value="XXXL">XXXL</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Instagram */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Instagram"
              placeholder="@votre_instagram"
              value={settings.profile.instagram}
              onChange={(e) => handleProfileChange('instagram', e.target.value)}
            />
          </Grid>

          {/* Mensurations */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Mensurations (en cm)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Buste"
                  type="number"
                  value={settings.profile.measurements.bust || ''}
                  onChange={(e) => handleMeasurementChange('bust', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 200 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Taille"
                  type="number"
                  value={settings.profile.measurements.waist || ''}
                  onChange={(e) => handleMeasurementChange('waist', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 200 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Hanche"
                  type="number"
                  value={settings.profile.measurements.hips || ''}
                  onChange={(e) => handleMeasurementChange('hips', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 200 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Taille (cm)"
                  type="number"
                  value={settings.profile.measurements.height || ''}
                  onChange={(e) => handleMeasurementChange('height', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 250 }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Styles préférés */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Styles préférés
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                'Classique', 'Moderne', 'Vintage', 'Romantique', 'Bohème', 
                'Chic', 'Décontracté', 'Élégant', 'Audacieux', 'Minimaliste',
                'Féminin', 'Masculin', 'Androgyne', 'Coloré', 'Monochrome'
              ].map((style) => (
                <Chip
                  key={style}
                  label={style}
                  onClick={() => handleStyleChange(style)}
                  color={settings.profile.preferredStyles.includes(style) ? 'primary' : 'default'}
                  variant={settings.profile.preferredStyles.includes(style) ? 'filled' : 'outlined'}
                  sx={{
                    backgroundColor: settings.profile.preferredStyles.includes(style) ? '#C9B6AB' : 'transparent',
                    color: settings.profile.preferredStyles.includes(style) ? 'white' : '#C9B6AB',
                    borderColor: '#C9B6AB',
                    '&:hover': {
                      backgroundColor: settings.profile.preferredStyles.includes(style) ? '#C1A08E' : 'rgba(201, 182, 171, 0.1)',
                    }
                  }}
                />
              ))}
            </Box>
          </Grid>

          {/* Bio */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Bio"
              multiline
              rows={3}
              placeholder="Parlez-nous de votre style et de vos goûts..."
              value={settings.profile.bio}
              onChange={(e) => handleProfileChange('bio', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notifications
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.notifications.email}
              onChange={() => handleNotificationChange('email')}
            />
          }
          label="Notifications par email"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.notifications.push}
              onChange={() => handleNotificationChange('push')}
            />
          }
          label="Notifications push"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.notifications.sms}
              onChange={() => handleNotificationChange('sms')}
            />
          }
          label="Notifications SMS"
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Confidentialité
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.privacy.showProfile}
              onChange={() => handlePrivacyChange('showProfile')}
            />
          }
          label="Afficher mon profil"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.privacy.showLocation}
              onChange={() => handlePrivacyChange('showLocation')}
            />
          }
          label="Afficher ma localisation"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.privacy.showRating}
              onChange={() => handlePrivacyChange('showRating')}
            />
          }
          label="Afficher mes notes"
        />
      </Paper>

      {/* Section Paiements Stripe */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PaymentIcon sx={{ mr: 1, color: '#C9B6AB' }} />
          <Typography variant="h6" sx={{ color: '#C9B6AB' }}>
            Activation des paiements
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Activez vos paiements pour recevoir automatiquement l'argent de vos locations de robes
        </Typography>

        {settings.stripe.connectedAccountId ? (
          <Box>
            {settings.stripe.onboardingCompleted && settings.stripe.chargesEnabled && settings.stripe.payoutsEnabled ? (
              <Card sx={{ backgroundColor: '#e8f5e9', border: '1px solid #4caf50', mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                      <Typography variant="h6" color="success.main">
                        Paiements activés
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      onClick={handleCheckStripeStatus}
                      disabled={stripeLoading}
                      startIcon={stripeLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    >
                      Vérifier
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ✅ Vos paiements sont activés ! Vous recevrez automatiquement l'argent de vos locations.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Compte Stripe: {settings.stripe.connectedAccountId.substring(0, 20)}...
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ff9800', mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WarningIcon sx={{ color: '#ff9800', mr: 1 }} />
                      <Typography variant="h6" color="warning.main">
                        Onboarding en cours
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      onClick={handleCheckStripeStatus}
                      disabled={stripeLoading}
                      startIcon={stripeLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    >
                      Vérifier le statut
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ⏳ Vous avez créé votre compte Stripe, mais l'onboarding n'est pas encore complété.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Complétez l'onboarding Stripe pour activer les paiements. Si vous avez déjà complété, cliquez sur "Vérifier le statut".
                  </Typography>
                  {settings.stripe.connectedAccountId && (
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        if (!user) return;
                        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                        try {
                          // Créer un nouveau lien d'onboarding
                          const response = await fetch(`${apiUrl}/api/stripe/create-connected-account`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              userId: user.uid,
                              userEmail: user.email
                            })
                          });
                          const data = await response.json();
                          if (data.success && data.onboardingUrl) {
                            // Rediriger vers l'onboarding dans le même onglet
                            window.location.href = data.onboardingUrl;
                          }
                        } catch (error) {
                          console.error('Erreur lors de la création du lien d\'onboarding:', error);
                        }
                      }}
                      sx={{ mt: 1 }}
                    >
                      Continuer l'onboarding
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography variant="h6" color="warning.main">
                  Paiements non activés
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pour recevoir vos paiements, vous devez compléter l'onboarding Stripe. 
                Cela prend moins de 5 minutes et nécessite :
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>Vos informations personnelles (nom, adresse, date de naissance)</li>
                <li>Votre IBAN pour les virements</li>
                <li>Une pièce d'identité (photo)</li>
              </Box>
              <Button
                variant="contained"
                onClick={handleActivatePayments}
                disabled={stripeLoading}
                startIcon={stripeLoading ? <CircularProgress size={20} /> : <OpenInNewIcon />}
                sx={{
                  backgroundColor: '#C9B6AB',
                  '&:hover': {
                    backgroundColor: '#C1A08E',
                  },
                }}
              >
                {stripeLoading ? 'Activation...' : 'Activer mes paiements'}
              </Button>
            </CardContent>
          </Card>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: '#C9B6AB',
            '&:hover': {
              backgroundColor: '#C1A08E',
            },
          }}
        >
          {saving ? <CircularProgress size={24} /> : 'Enregistrer'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleLogout}
          sx={{
            ml: 2,
            borderColor: '#C9B6AB',
            color: '#C9B6AB',
            '&:hover': {
              borderColor: '#C1A08E',
              backgroundColor: 'rgba(201, 182, 171, 0.1)',
              color: '#C1A08E',
            },
          }}
        >
          Déconnexion
        </Button>
      </Box>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Paramètres enregistrés avec succès
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Panel de test des notifications */}
      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#a5816d' }}>
          🧪 Test des notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Testez les différents types de notifications disponibles dans l'application
        </Typography>
        <NotificationTestPanel />
      </Paper>
    </Box>
  );
};

export default SettingsTab; 